import type { Env, PartnerConfig, PartnerDelivery, ResolvedTenant } from "./schema";
import { partners } from "./tenants";
import { tenantMap } from "./tenant-map";
import { assetUrl } from "./assets";

// マージは行わない（ROXX 既定は各アプリが持つ）。ここでの「解決」は
//   1. 提携先のブランド（環境に依らない）に、その環境の面の在り処を重ねる
//   2. ブランドのアセット相対パスを、その環境の配信 URL に変換する
// の2つだけ。**出力の形（ResolvedTenant / generated JSON）は消費側との互換のため変えない。**

const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** 面の在り処に属するキー。ブランド側に書かれていたら設定の誤りとして落とす。 */
const deliveryKeys = {
  company: ["adminHost", "viewerHost", "portalHost"],
  candidate: ["examHost"],
} as const;

/** 送信元に属するキー。ブランド側（`<scope>.mail`）に書かれていたら設定の誤りとして落とす。 */
const deliveryMailKeys = {
  company: ["accountIssueFrom", "completionNoticeFrom"],
  candidate: ["remindNoticeFrom"],
} as const;

/**
 * ブランドに面の在り処が混ざっていないことを確かめる。
 *
 * 混ざると「環境で変わる値が環境非依存の場所にある」状態に戻り、stg と prod で同じホストを
 * 指すという不具合を再発させる（実際に起きた）。覚えておく規則にせず、生成時に落とす。
 */
function assertBrandHasNoDelivery(partnerKey: string, brand: PartnerConfig): void {
  const found: string[] = [];
  for (const [scope, keys] of Object.entries(deliveryKeys)) {
    const domain = (brand as Record<string, { domain?: Record<string, unknown> }>)[scope]?.domain;
    if (!domain) continue;
    for (const key of keys) {
      if (has(domain, key)) found.push(`${scope}.domain.${key}`);
    }
  }
  // 送信元も同じ理由で環境ごとに書く。ブランド側に置くと、検証環境が提携先の本番ドメインを
  // 名乗ることになる（送信ドメイン認証を満たせず送れないうえ、名乗ってはならない）。
  for (const [scope, keys] of Object.entries(deliveryMailKeys)) {
    const mail = (brand as Record<string, { mail?: Record<string, unknown> }>)[scope]?.mail;
    if (!mail) continue;
    for (const key of keys) {
      if (has(mail, key)) found.push(`${scope}.mail.${key}`);
    }
  }
  if (found.length > 0) {
    throw new Error(
      `面の在り処は brand ではなく delivery に書く: partner=${partnerKey} 該当=${found.join(", ")}` +
        `（tenants/${partnerKey}.ts の delivery.<env> へ移してください）`,
    );
  }
}

/** ブランドに、その環境の面の在り処（配信ホスト）を重ねた差分を返す。 */
function withDelivery(brand: PartnerConfig, delivery: PartnerDelivery): PartnerConfig {
  const companyDomain = {
    adminHost: delivery.adminHost,
    ...(delivery.viewerHost !== undefined ? { viewerHost: delivery.viewerHost } : {}),
    ...(delivery.portalHost !== undefined ? { portalHost: delivery.portalHost } : {}),
    ...(brand.company?.domain ?? {}),
  };
  return {
    ...brand,
    company: {
      ...brand.company,
      domain: companyDomain,
      mail: {
        accountIssueFrom: delivery.accountIssueFrom,
        completionNoticeFrom: delivery.completionNoticeFrom,
        ...(brand.company?.mail ?? {}),
      },
    },
    candidate: {
      ...brand.candidate,
      domain: {
        examHost: delivery.examHost,
        ...(brand.candidate?.domain ?? {}),
      },
      mail: {
        remindNoticeFrom: delivery.remindNoticeFrom,
        ...(brand.candidate?.mail ?? {}),
      },
    },
  };
}

/** company.brand のアセット相対パス（存在すれば）を配信 URL に変換した差分を返す。 */
function withAssetUrls(overrides: PartnerConfig, baseUrl: string): PartnerConfig {
  const brand = overrides.company?.brand;
  if (!brand) return overrides;
  const toUrl = (v: string | null | undefined) =>
    typeof v === "string" ? assetUrl(baseUrl, v) : v;
  return {
    ...overrides,
    company: {
      ...overrides.company,
      brand: {
        ...brand,
        ...(brand.serviceNameLogo !== undefined ? { serviceNameLogo: toUrl(brand.serviceNameLogo) } : {}),
        ...(brand.logo !== undefined ? { logo: toUrl(brand.logo) } : {}),
        ...(brand.logoPng !== undefined ? { logoPng: toUrl(brand.logoPng) } : {}),
        ...(brand.favicon !== undefined ? { favicon: toUrl(brand.favicon) } : {}),
      },
    },
  };
}

/** env + tenant_id → partnerKey（未登録なら null、prototype 汚染安全）。 */
export function resolvePartnerKey(env: Env, tenantId: string): string | null {
  const m = has(tenantMap, env) ? tenantMap[env] : undefined;
  return m && has(m, tenantId) ? m[tenantId] : null;
}

/** env + tenant_id を解決（ブランド + その環境の面の在り処 + アセット URL）。未登録なら null。 */
export function resolveTenant(env: Env, tenantId: string): ResolvedTenant | null {
  const partnerKey = resolvePartnerKey(env, tenantId);
  if (!partnerKey || !has(partners, partnerKey)) return null;
  const partner = partners[partnerKey];

  // 型上 Record<Env, PartnerDelivery> で保証されるが、env 追加漏れ等の設定ミスを
  // 握り潰さず（silent に相対パスや undefined を返さず）、ビルド時に明確に落とす。
  const delivery = partner.delivery?.[env];
  if (!delivery) {
    throw new Error(
      `delivery 未定義: partner=${partnerKey} env=${env}（tenants/${partnerKey}.ts の delivery に ${env} を定義してください）`,
    );
  }
  const baseUrl = delivery.assetBaseUrl;
  if (typeof baseUrl !== "string" || baseUrl.trim().length === 0) {
    throw new Error(
      `assetBaseUrl 未定義: partner=${partnerKey} env=${env}（tenants/${partnerKey}.ts の delivery.${env}.assetBaseUrl を定義してください）`,
    );
  }

  assertBrandHasNoDelivery(partnerKey, partner.brand);

  const config = withAssetUrls(withDelivery(partner.brand, delivery), baseUrl);
  return { env, tenantId, partnerKey, config };
}

/** 全 (env, tenant_id) の解決結果。 */
export function resolveAll(): ResolvedTenant[] {
  const out: ResolvedTenant[] = [];
  for (const env of Object.keys(tenantMap) as Env[]) {
    for (const tenantId of Object.keys(tenantMap[env])) {
      const r = resolveTenant(env, tenantId);
      if (r) out.push(r);
    }
  }
  return out;
}
